import 'uplot/dist/uPlot.min.css';

import { RotateCcw } from 'lucide-react';
import { type ReactNode,useEffect, useMemo, useRef } from 'react';
import type uPlot from 'uplot';

import type { TooltipInfo } from '../_shared';
import {
  ChartTooltip,
  convertToUPlotData,
  formatDayLabel,
  formatHourLabel,
  formatWeekLabel,
  useUPlot,
  wheelZoomPlugin,
} from '../_shared';
import { createLineChartResetController } from './reset-controller';
import { getIsResetEnabled } from './reset-state';
import type { LineChartProps } from './types';
import { convertToUPlotLineSeries, splinePath } from './uplot-line-series';

const defaultTooltipContent = (info: TooltipInfo): ReactNode => (
  <div className="text-sm">
    {info.points.map((point) => (
      <div key={point.seriesId} className="flex items-center gap-2 py-0.5">
        <span className="inline-block h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: point.color }} />
        <span className="text-gray-700">{point.label}</span>
        <span className="ml-auto font-medium">{point.value}</span>
      </div>
    ))}
  </div>
);

const EMPTY_RECT: DOMRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  toJSON: () => ({}),
};

export const LineChart = ({
  series,
  xAxis,
  hiddenSeriesIds,
  enableZoom = false,
  tooltipContent,
  onReset,
  onResetControllerChange,
}: LineChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  // 전체 시리즈 데이터 기준 y축 최대값 — hidden 시리즈 포함
  const globalYMaxRef = useRef(0);
  globalYMaxRef.current = useMemo(() => {
    let max = 0;
    for (const s of series) {
      for (const point of s.data) {
        if (point.y > max) max = point.y;
      }
    }
    return max;
  }, [series]);

  const data = useMemo(() => convertToUPlotData(series, hiddenSeriesIds), [hiddenSeriesIds, series]);

  const seriesConfig = useMemo(() => convertToUPlotLineSeries(series, splinePath), [series]);

  const plugins = useMemo(() => (enableZoom ? [wheelZoomPlugin()] : []), [enableZoom]);

  const options = useMemo<uPlot.Options>(
    () => ({
      width: 100,
      height: 100,
      legend: { show: false },
      series: seriesConfig,
      scales: {
        x: { time: false },
        y: {
          auto: true,
          range: (_chart, min, max) => {
            const globalMax = globalYMaxRef.current;
            const effectiveMax = Math.max(
              Number.isFinite(max) ? max : 0,
              globalMax,
            );
            return [0, Math.max(effectiveMax * 1.1, 1)];
          },
        },
      },
      axes: [
        {
          values: (_chart, splits) =>
            splits.map((value) => {
              const timestampMs = Number(value) * 1000;
              const fallbackLabel =
                xAxis?.timeUnit === 'day'
                  ? formatDayLabel(timestampMs)
                  : xAxis?.timeUnit === 'week'
                    ? formatWeekLabel(timestampMs)
                    : formatHourLabel(timestampMs);

              return xAxis?.formatLabel ? xAxis.formatLabel(timestampMs) : fallbackLabel;
            }),
        },
        {
          values: (_chart, splits) => splits.map((value) => String(Math.round(Number(value)))),
        },
      ],
      cursor: {
        lock: false,
        focus: {
          alpha: 0.2,
          prox: 30,
        },
      },
      plugins,
    }),
    [plugins, seriesConfig, xAxis],
  );

  const uplotState = useUPlot({
    targetRef,
    options,
    data,
    series,
    hiddenSeriesIds,
  });

  const isResetEnabled = getIsResetEnabled({
    canResetZoom: uplotState.zoomControls.canReset,
    hiddenSeriesIds,
  });
  const resetController = useMemo(
    () =>
      createLineChartResetController({
        enabled: isResetEnabled,
        onReset,
        resetZoom: uplotState.zoomControls.reset,
      }),
    [isResetEnabled, onReset, uplotState.zoomControls.reset],
  );

  const containerRect = containerRef.current?.getBoundingClientRect() ?? EMPTY_RECT;

  useEffect(() => {
    onResetControllerChange?.(resetController);
  }, [onResetControllerChange, resetController]);

  return (
    <div ref={containerRef} className="relative h-full w-full">
      {(enableZoom || onReset) && (
        <button
          type="button"
          onClick={resetController.reset}
          disabled={!resetController.enabled}
          className="absolute right-3 top-3 z-10 inline-flex h-8 items-center gap-1 rounded-md border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
      )}
      <div ref={targetRef} className="h-full w-full" {...uplotState.interactionHandlers} />
      {uplotState.tooltipInfo && (
        <ChartTooltip
          info={uplotState.tooltipInfo}
          containerRect={containerRect}
          onMouseEnter={uplotState.tooltipHandlers.onMouseEnter}
          onMouseLeave={uplotState.tooltipHandlers.onMouseLeave}
        >
          {tooltipContent ? (info) => tooltipContent(info, uplotState.tooltipActions) : defaultTooltipContent}
        </ChartTooltip>
      )}
    </div>
  );
};
